import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/client';
import { useCustomerAuth } from './CustomerAuthContext';

const CartContext = createContext(null);
const STORAGE_KEY = '3dlavka_cart';

function loadLocal() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(x => x && Number(x.model_id) > 0 && Number(x.quantity) > 0)
      .map(x => ({ model_id: Number(x.model_id), quantity: Number(x.quantity) }));
  } catch { return []; }
}

function saveLocal(items) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch {}
}

function mergeCarts(a, b) {
  const map = new Map();
  for (const it of [...a, ...b]) {
    const cur = map.get(it.model_id) || 0;
    map.set(it.model_id, cur + it.quantity);
  }
  return Array.from(map.entries()).map(([model_id, quantity]) => ({ model_id, quantity }));
}

export function CartProvider({ children }) {
  const { customer } = useCustomerAuth();
  const [items, setItems] = useState(loadLocal());
  const debounceRef = useRef(null);

  // Sync при логине: мерж локальной с серверной
  useEffect(() => {
    if (!customer) return;
    let cancelled = false;
    api.get('/customers/me/cart')
      .then(r => {
        if (cancelled) return;
        const local = loadLocal();
        const merged = mergeCarts(r.data || [], local);
        setItems(merged);
        saveLocal(merged);
        // Если что-то добавилось из локальной — пушим обратно
        api.put('/customers/me/cart', { cart: merged }).catch(() => {});
      })
      .catch(() => {});
    return () => { cancelled = true; };
  // eslint-disable-next-line
  }, [customer?.id]);

  // Сохранение в localStorage всегда; в БД — для авторизованных, дебаунс 400мс
  useEffect(() => {
    saveLocal(items);
    if (!customer) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      api.put('/customers/me/cart', { cart: items }).catch(() => {});
    }, 400);
  // eslint-disable-next-line
  }, [items, customer?.id]);

  const addItem = useCallback((model_id, quantity = 1) => {
    setItems(prev => {
      const existing = prev.find(x => x.model_id === model_id);
      if (existing) return prev.map(x => x.model_id === model_id ? { ...x, quantity: x.quantity + quantity } : x);
      return [...prev, { model_id, quantity }];
    });
  }, []);

  const setQuantity = useCallback((model_id, quantity) => {
    setItems(prev => quantity <= 0
      ? prev.filter(x => x.model_id !== model_id)
      : prev.map(x => x.model_id === model_id ? { ...x, quantity } : x));
  }, []);

  const removeItem = useCallback((model_id) => {
    setItems(prev => prev.filter(x => x.model_id !== model_id));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const totalCount = items.reduce((s, x) => s + x.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addItem, setQuantity, removeItem, clear, totalCount }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
